import { Box } from '@mui/material';
import { DragHandle } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { approvalStatusMark } from '~/components/Structures/approvalStatusMark';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { BlendCandidate } from '../adapters/types';
import { READOUT_DIM, WARNING } from '../chrome';

type BlendBinding = BlendCandidate['binding'];

/** Width of the mark's column, in CSS pixels, whatever the glyph's size. */
const MARK_WIDTH_PX = 16;

const APPROVAL_COLOURS: Record<DICOMApprovalStatus, string> = {
    [DICOMApprovalStatus.APPROVED]: 'success.light',
    [DICOMApprovalStatus.UNAPPROVED]: WARNING,
    [DICOMApprovalStatus.REJECTED]: 'error.light',
};

const TEST_IDS: Record<DICOMApprovalStatus, string> = {
    [DICOMApprovalStatus.APPROVED]: 'blend-approval-approved',
    [DICOMApprovalStatus.UNAPPROVED]: 'blend-approval-unapproved',
    [DICOMApprovalStatus.REJECTED]: 'blend-approval-rejected',
};

/**
 * How a secondary image lies on the row's own, as one glyph: the approval
 * status of the registration that places it, or an equals sign for an image
 * on the same frame of reference, which has no registration to approve.
 * Decorative: whoever shows it also says the same in words.
 */
export default function BlendBindingMark({
    binding,
    sizePx,
}: {
    readonly binding: BlendBinding;
    /** Size of the glyph, in CSS pixels. */
    readonly sizePx: number;
}) {
    const sameFrame = binding.kind !== 'mapped';
    const Icon = sameFrame
        ? DragHandle
        : approvalStatusMark(binding.approvalStatus).Icon;
    return (
        <Box
            aria-hidden
            data-testid={
                sameFrame
                    ? 'blend-approval-same-frame'
                    : TEST_IDS[binding.approvalStatus]
            }
            sx={{
                display: 'flex',
                flexShrink: 0,
                justifyContent: 'center',
                width: MARK_WIDTH_PX,
                color: sameFrame
                    ? READOUT_DIM
                    : APPROVAL_COLOURS[binding.approvalStatus],
            }}
        >
            <Icon sx={{ fontSize: sizePx, width: sizePx, height: sizePx }} />
        </Box>
    );
}

/**
 * The words for how a fused image lies on the row's own: the registration's
 * label and its approval status, or "Same frame". The label is patient data,
 * masked with the privacy toggle.
 */
export function BlendBindingSummary({
    binding,
    registrationLabel,
}: {
    readonly binding: BlendBinding;
    readonly registrationLabel: string | undefined;
}) {
    if (binding.kind !== 'mapped') {
        return <FormattedMessage id="fusion.sameFrame" />;
    }
    return (
        <FormattedMessage
            id="fusion.registrationSummary"
            values={{
                hasLabel: registrationLabel ? 'yes' : 'no',
                label: (
                    <SensitiveInfoTypography>
                        {registrationLabel}
                    </SensitiveInfoTypography>
                ),
                status: (
                    <FormattedMessage
                        id={approvalStatusMark(binding.approvalStatus).labelId}
                    />
                ),
            }}
        />
    );
}
